import { BodyText } from "@/components/ui";
import { copayAmountDisplay } from "./CopayTable";
import { AltBadge } from "./PayerAlternatives";
import type { TherapeuticAlternative } from "./network/useFormularyLookup";

// Maps formulary status text to a numeric rank for comparison.
// Lower rank = more preferred (Preferred Level 1 is best).
// Non-preferred statuses use high numbers so preferred alts always rank better.
const formularyStatusRank = (status: string | null): number => {
  if (!status) return Number.POSITIVE_INFINITY;
  const s = status.toLowerCase();
  if (s.includes("preferred level")) {
    const match = s.match(/(\d+)$/);
    return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
  }
  if (s.includes("not preferred")) return 100;
  if (s.includes("non formulary")) return 101;
  if (s.includes("not reimbursable")) return 102;
  return Number.POSITIVE_INFINITY;
};

const TherapeuticAlternativeRow = ({
  alt,
  selectedFormularyStatus,
}: {
  alt: TherapeuticAlternative;
  selectedFormularyStatus: string | null;
}) => {
  const showStrength =
    alt.displayStrength != null &&
    formularyStatusRank(alt.formularyStatus) <
      formularyStatusRank(selectedFormularyStatus);

  return (
    <div className="flex flex-col gap-0.5 text-sm py-1.5 px-3 border-b last:border-0">
      <div className="flex items-baseline gap-1.5">
        <span>{alt.fullDisplayString || alt.displayName}</span>
        {showStrength && (
          <span className="text-xs text-tertiary">{alt.displayStrength}</span>
        )}
      </div>
      <div className="flex gap-2 text-xs">
        <AltBadge value={alt.formularyStatus} color="green" />
        <AltBadge value={copayAmountDisplay(alt.copay)} color="blue" />
      </div>
    </div>
  );
};

interface TherapeuticAlternativesProps {
  alternatives: TherapeuticAlternative[];
  selectedFormularyStatus: string | null;
}

export const TherapeuticAlternatives = ({
  alternatives,
  selectedFormularyStatus,
}: TherapeuticAlternativesProps) => {
  if (alternatives.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <BodyText size="sm" className="font-medium">
        Therapeutic Alternatives
      </BodyText>
      <div className="rounded-md border overflow-hidden">
        {alternatives.map((alt) => (
          <TherapeuticAlternativeRow
            key={alt.ndc ?? alt.displayName}
            alt={alt}
            selectedFormularyStatus={selectedFormularyStatus}
          />
        ))}
      </div>
    </div>
  );
};
